import axios from 'axios';
import joinNonEmptyParams from '@/utils/joinNonEmptyParams';
import { createApiRequest } from '@/utils/services';
import dowloadFileFromUrl from './downloadFileFromUrl';
import type UploadContexts from './UploadContexts';

type FileUploadParams = {
  context: UploadContexts;
  errorsS3Path?: string;
  ownerOrgId: string;
};

type FileAccessParams = FileUploadParams & { uploadId: string };

// Upload a file
const getPresignedPostUrl = (
  params: FileUploadParams & { fileName: string },
): Promise<{
  data: {
    presignedPost: string;
    uploadId: string;
  };
}> =>
  createApiRequest({
    method: 'get',
    path: `presigned-post?${joinNonEmptyParams(params)}`,
  });

export const uploadFile = async (
  file: File,
  params: FileUploadParams,
): Promise<{ presignedPost: string; uploadId: string }> => {
  const { data } = await getPresignedPostUrl({
    ...params,
    fileName: file.name,
  });
  const { presignedPost, uploadId } = data;
  await axios.put(presignedPost, file, {
    headers: { 'Content-Type': 'application/binary' },
  });
  return { presignedPost, uploadId };
};

// Download file
export const getPresignedGetUrl = (
  params: FileAccessParams,
): Promise<{ data: { presignedGet: string } }> =>
  createApiRequest({
    method: 'get',
    path: `presigned-get?${joinNonEmptyParams(params)}`,
  });

export const getFile = async (params: FileAccessParams) => {
  const { data } = await getPresignedGetUrl(params);
  return axios({
    url: data.presignedGet,
    method: 'GET',
    responseType: 'blob',
  });
};

export const downloadFile = async (
  params: FileAccessParams,
  fileName: string,
) => {
  const { data } = await getPresignedGetUrl(params);
  const { presignedGet } = data;
  return dowloadFileFromUrl(presignedGet, fileName);
};

// Delete file
const getPresignedDeleteUrl = (
  params: FileAccessParams,
): Promise<{ data: { presignedDelete: string } }> =>
  createApiRequest({
    method: 'get',
    path: `presigned-delete?${joinNonEmptyParams(params)}`,
  });

export const deleteFile = async (params: FileAccessParams) => {
  const { data } = await getPresignedDeleteUrl(params);
  const { presignedDelete } = data;
  return axios.delete(presignedDelete);
};
